/**
 * ConfirmPH: Business support tickets page.
 *
 * DEPRECATED (Support V1 closure audit, 2026-08-16): kept read-only for
 * ticket history. New requests go through Support (support-cases) at
 * /app/help; the backend rejects new tickets/replies here with a 410.
 * NOT a customer↔business chat, either historically or in its replacement.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useOperatorContacts } from "@/lib/operatorContacts";
import {
  HelpCircle, Headset, Plus, Loader2, ChevronLeft, Mail,
} from "lucide-react";

const CATEGORY_LABEL = {
  billing: "Billing",
  bug: "Bug",
  feature_request: "Feature request",
  account: "Account",
  booking_issue: "Booking issue",
  technical_problem: "Technical problem",
  abuse_report: "Abuse / report",
  other: "Other",
};

const PRIORITY_LABEL = {
  low: "Low", normal: "Normal", high: "High", urgent: "Urgent",
};

const STATUS_TONE = {
  open:                "bg-amber-100 text-amber-800 border-amber-200",
  waiting_for_support: "bg-amber-100 text-amber-800 border-amber-200",
  waiting_for_business:"bg-indigo-100 text-indigo-800 border-indigo-200",
  resolved:            "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed:              "bg-slate-200 text-slate-700 border-slate-300",
};

export default function SupportTickets() {
  return <SupportInbox />;
}

function SupportInbox() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      const { data } = await api.get("/support/tickets", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  const contacts = useOperatorContacts();

  return (
    <div className="space-y-5 max-w-4xl" data-testid="support-tickets-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold inline-flex items-center gap-2">
            <Headset className="w-6 h-6 text-emerald-700" /> Support
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-prose">
            This ticket history is kept for reference. Need to email instead? <a href={`mailto:${contacts.support}`}
              className="underline inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {contacts.support}</a>.
          </p>
        </div>
        <Link to="/app/help"
                className="px-3 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 inline-flex items-center gap-2"
                data-testid="support-new-ticket-btn">
          <Plus className="w-4 h-4" /> New request
        </Link>
      </header>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800"
           data-testid="support-legacy-banner">
        This channel no longer accepts new messages: it's kept read-only for past tickets.
        For new requests, use <Link to="/app/help" className="underline font-semibold">Help &amp; Support</Link>.
      </div>

      <div className="flex flex-wrap gap-2" data-testid="support-filters">
        {[
          { v: "",                   l: "All" },
          { v: "active",             l: "Active" },
          { v: "open",               l: "Open" },
          { v: "waiting_for_support",l: "Waiting on us" },
          { v: "waiting_for_business",l:"Waiting on you" },
          { v: "resolved",           l: "Resolved" },
          { v: "closed",             l: "Closed" },
        ].map(({ v, l }) => (
          <button key={v} onClick={() => setStatus(v)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    status === v
                      ? "bg-[#1B4332] text-white border-[#1B4332]"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid={`support-filter-${v || "all"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-10 text-center text-sm text-slate-600"
             data-testid="support-empty">
          <HelpCircle className="w-6 h-6 mx-auto mb-2 text-slate-400" />
          No ticket history here. Use Help &amp; Support for new requests.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="support-tickets-list">
          {data.items.map(t => (
            <li key={t.id}>
              <Link to={`/app/support/${t.id}`}
                    className="block rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-slate-300"
                    data-testid={`support-ticket-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.subject}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {CATEGORY_LABEL[t.category] || t.category}
                      <span className="mx-1.5">·</span>
                      Priority: {PRIORITY_LABEL[t.priority] || t.priority}
                      <span className="mx-1.5">·</span>
                      Updated {t.updated_at?.slice(0, 10)}
                      <span className="mx-1.5">·</span>
                      {t.message_count} message{t.message_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_TONE[t.status] || ""}`}>
                    {t.status.replace(/_/g, " ")}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------
export function SupportTicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setTicket(data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/app/support");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const close = async () => {
    if (!window.confirm("Close this ticket?")) return;
    try {
      const { data } = await api.post(`/support/tickets/${id}/close`);
      setTicket(data);
      toast.success("Ticket closed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !ticket) {
    return (
      <div className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="support-ticket-detail">
      <Link to="/app/support"
            className="text-sm text-slate-600 hover:text-slate-900 underline inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> Back to tickets
      </Link>

      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold">{ticket.subject}</h1>
          <div className="text-xs text-slate-500 mt-1">
            {CATEGORY_LABEL[ticket.category] || ticket.category}
            <span className="mx-1.5">·</span>
            Priority: {PRIORITY_LABEL[ticket.priority] || ticket.priority}
            <span className="mx-1.5">·</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_TONE[ticket.status] || ""}`}>
              {ticket.status.replace(/_/g, " ")}
            </span>
          </div>
        </div>
        {ticket.status !== "closed" && (
          <button onClick={close}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"
                  data-testid="support-close-btn">
            Close ticket
          </button>
        )}
      </header>

      <ul className="space-y-3" data-testid="support-thread">
        {ticket.messages.map(m => (
          <li key={m.id}
              className={`rounded-xl border p-3 ${
                m.sender_role === "support"
                  ? "border-indigo-200 bg-indigo-50/60"
                  : "border-slate-200 bg-white"
              }`}
              data-testid={`support-message-${m.id}`}>
            <div className="text-[11px] text-slate-500 mb-1">
              <span className="font-semibold text-slate-700">{m.sender_name}</span>
              <span className="ml-1">({m.sender_role === "support" ? "ConfirmPH Support" : "You / your team"})</span>
              <span className="ml-2">{m.created_at?.replace("T", " ").slice(0, 16)}</span>
            </div>
            <p className="text-sm whitespace-pre-line">{m.text}</p>
          </li>
        ))}
      </ul>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600"
           data-testid="support-reply-disabled-banner">
        This channel no longer accepts new replies. <Link to="/app/help" className="underline">Go to Help &amp; Support</Link> if you need more help.
      </div>
    </div>
  );
}
